
import { ClauseAnalysis, RiskLevel, Language, ReviewConfig } from "../types";

const API_BASE_URL = "https://api.openai-next.com/v1";
const API_KEY = (import.meta.env?.VITE_OPENAI_API_KEY as string) || 'sk-bZb5zjBbGSg6xRjJ9e9a6538Bb1b4b8498446aCfE6F3D685';

// 支持的模型列表
const MODELS = {
  // 推理模型（支持思维链）
  REASONING: {
    'gpt-5-thinking': 'gpt-5-thinking',
    'o1-preview': 'o1-preview',
    'o1-mini': 'o1-mini',
    'o3': 'o3',
    'o3-mini': 'o3-mini'
  },
  // 标准模型
  STANDARD: {
    'gemini-3-flash-preview': 'gemini-3-flash-preview',
    'gpt-4o': 'gpt-4o',
    'gpt-4o-mini': 'gpt-4o-mini'
  }
};

// 默认使用推理模型（支持思维链）
const DEFAULT_MODEL = 'gpt-5-thinking';

async function callOpenAI(
  messages: Array<{ role: string; content: string }>, 
  responseFormat?: { type: string }, 
  enableThinking: boolean = false,
  modelName?: string
) {
  try {
    // 确定使用的模型
    const selectedModel = modelName || DEFAULT_MODEL;
    const isReasoningModel = Object.values(MODELS.REASONING).includes(selectedModel as any);
    
    const requestBody: any = {
      model: selectedModel,
      stream: false,
      messages
    };

    // 对于推理模型（o1, o3, gpt-5-thinking），不需要额外的思维链参数
    // 它们原生支持推理过程，但可能不支持 response_format
    if (enableThinking && !isReasoningModel) {
      // 仅对非推理模型（如 Gemini）添加思维链参数
      if (selectedModel.includes('gemini')) {
        requestBody.thinking_level = "medium";
        requestBody.includeThoughts = true;
      }
    }

    // 推理模型通常不支持 response_format，需要让模型在提示词中返回 JSON
    // 只有在需要 JSON 格式且不是推理模型时才添加 response_format
    if (responseFormat && !isReasoningModel) {
      requestBody.response_format = responseFormat;
    }

    console.log('API Request:', {
      url: `${API_BASE_URL}/chat/completions`,
      model: requestBody.model,
      isReasoningModel: isReasoningModel,
      hasResponseFormat: !!responseFormat,
      enableThinking: enableThinking,
      messageCount: messages.length
    });

    const response = await fetch(`${API_BASE_URL}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = errorText;
      }

      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });

      // 如果模型不存在或不可用，尝试回退到其他模型
      const errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
      const isModelError = errorMessage.includes('model') || 
                          errorMessage.includes('not found') || 
                          errorMessage.includes('invalid') ||
                          (response.status === 404);
      
      if (isModelError && selectedModel === DEFAULT_MODEL) {
        // 尝试回退到其他推理模型
        const fallbackModels = ['o1-preview', 'o1-mini', 'o3', 'o3-mini', 'gemini-3-flash-preview'];
        for (const fallbackModel of fallbackModels) {
          console.warn(`Model ${selectedModel} not available, trying fallback: ${fallbackModel}`);
          try {
            return await callOpenAI(messages, responseFormat, enableThinking, fallbackModel);
          } catch (fallbackError) {
            console.warn(`Fallback model ${fallbackModel} also failed, trying next...`);
            continue;
          }
        }
      }
      
      // 如果错误是因为 response_format 或 thinking 参数不支持，且我们使用了它们，则重试不使用这些参数
      if ((responseFormat || enableThinking) && (
        errorMessage.includes('response_format') || 
        errorMessage.includes('thinking') ||
        errorMessage.includes('includeThoughts') ||
        errorMessage.includes('unsupported') ||
        (response.status === 400 && !errorMessage.includes('empty') && !isModelError)
      )) {
        console.warn('API may not support some parameters, retrying without them...', {
          hadResponseFormat: !!responseFormat,
          hadThinking: enableThinking,
          model: selectedModel
        });
        // 递归调用，但不使用这些参数，保持模型不变
        return await callOpenAI(messages, undefined, false, selectedModel);
      }

      throw new Error(`API Error: ${response.status} - ${errorMessage}`);
    }

    const data = await response.json();
    console.log('API Response structure:', {
      hasChoices: !!data.choices,
      choicesLength: data.choices?.length,
      firstChoice: data.choices?.[0] ? {
        hasMessage: !!data.choices[0].message,
        hasContent: !!data.choices[0].message?.content,
        contentLength: data.choices[0].message?.content?.length || 0,
        hasThoughts: !!data.choices[0].message?.thoughts
      } : null,
      hasThoughts: enableThinking && !!data.thoughts
    });

    // 提取思维链（如果存在）
    let thoughts = '';
    if (enableThinking) {
      // 尝试从不同位置提取思维链
      if (data.thoughts) {
        thoughts = typeof data.thoughts === 'string' ? data.thoughts : JSON.stringify(data.thoughts);
      } else if (data.choices?.[0]?.message?.thoughts) {
        thoughts = typeof data.choices[0].message.thoughts === 'string' 
          ? data.choices[0].message.thoughts 
          : JSON.stringify(data.choices[0].message.thoughts);
      } else if (data.choices?.[0]?.message?.parts) {
        // Gemini API 可能将思维链放在 parts 中
        const thoughtParts = data.choices[0].message.parts
          .filter((part: any) => part.thought || part.thoughts)
          .map((part: any) => part.thought || part.thoughts)
          .join('\n');
        if (thoughtParts) {
          thoughts = thoughtParts;
        }
      }
      
      if (thoughts) {
        console.log('Extracted thoughts from API response:', thoughts.substring(0, 200));
      }
    }

    const content = data.choices?.[0]?.message?.content || '';
    const finishReason = data.choices?.[0]?.finish_reason;

    if (!content || content.trim() === '') {
      console.error('Empty content from API. Full response:', JSON.stringify(data, null, 2));
      console.error('API Response details:', {
        model: data.model,
        finish_reason: finishReason,
        usage: data.usage,
        hasChoices: !!data.choices,
        choicesLength: data.choices?.length,
        firstChoiceMessage: data.choices?.[0]?.message
      });
      
      // 如果 finish_reason 不是 STOP，可能是被过滤或其他原因
      if (finishReason && finishReason !== 'stop' && finishReason !== 'STOP') {
        throw new Error(`API response was filtered or stopped: finish_reason=${finishReason}. Please check model configuration or try a different model.`);
      }
      
      // 如果使用了 response_format 或 thinking 参数但返回空内容，尝试重试不使用它们
      if (responseFormat || enableThinking) {
        console.warn('Empty content with special parameters, retrying without them...', {
          hadResponseFormat: !!responseFormat,
          hadThinking: enableThinking,
          model: selectedModel
        });
        return await callOpenAI(messages, undefined, false, selectedModel);
      }
      
      // 如果finish_reason是STOP但content为空，可能是模型问题，尝试调整参数重试
      if (finishReason === 'stop' || finishReason === 'STOP') {
        console.warn('Empty content with STOP finish_reason, this may indicate a model issue. Please check API configuration.');
        throw new Error('API returned empty content despite successful completion (finish_reason=STOP). This may indicate a model configuration issue. Please check the API settings or try a different model.');
      }
      
      throw new Error('API returned empty content. This may be due to content filtering or model issues.');
    }

    // 如果启用了思维链且有思维数据，可以在这里处理
    // 注意：思维链应该在 JSON 响应的 thoughtChain 字段中，这里只是记录日志
    if (enableThinking && thoughts) {
      console.log('Model thinking process available (length:', thoughts.length, ')');
    }

    return content;
  } catch (error: any) {
    console.error('callOpenAI error:', {
      message: error?.message,
      stack: error?.stack,
      name: error?.name
    });
    throw error;
  }
}

export async function analyzeClause(clauseText: string, lang: Language): Promise<Partial<ClauseAnalysis>> {
  try {
    const systemPrompt = lang === 'zh'
      ? "你是一位拥有20年经验的资深商业律师。你的分析应当尖锐、务实且具有保护性。不要说废话，直接给出高价值建议。必须使用简体中文。你必须以纯JSON格式返回分析结果，不要包含任何其他文本。JSON必须包含以下字段：category（条款的法律类别，例如：责任限制、不可抗力、争议解决）、riskLevel（必须是LOW、MEDIUM、HIGH或CRITICAL之一）、riskDescription（该条款对己方可能造成的具体法律或商业损害说明，必须详细具体）、suggestedAction（具体的修改动作或谈判策略建议）、revisedText（建议修订后的完整条款文本，应符合法律术语规范）。"
      : "You are a senior commercial lawyer with 20 years of experience. Your analysis should be sharp, pragmatic, and protective. Provide high-value insights directly. Must use English. You must return ONLY a valid JSON object, no other text. The JSON must contain: category (legal category like liability limitation, force majeure, dispute resolution), riskLevel (must be LOW, MEDIUM, HIGH, or CRITICAL), riskDescription (detailed explanation of potential legal or commercial harm), suggestedAction (specific amendment action or negotiation strategy), revisedText (full revised clause text using standard legal terminology).";

    const userPrompt = lang === 'zh'
      ? `作为资深法律顾问，请对以下合同条款进行深度审查并识别风险：\n\n"${clauseText}"`
      : `As a senior legal counsel, please perform a deep review of the following clause and identify risks:\n\n"${clauseText}"`;

    console.log('Calling API for clause analysis...');
    const content = await callOpenAI([
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt }
    ], undefined, false, DEFAULT_MODEL);

    console.log('API Response content:', content);

    if (!content || content.trim() === '') {
      throw new Error('API returned empty content');
    }

    let parsed;
    try {
      // 先尝试直接解析
      parsed = JSON.parse(content);
    } catch (parseError) {
      console.warn('Direct JSON parse failed, trying to extract JSON...', parseError);

      // 尝试移除 markdown 代码块标记
      let cleanedContent = content.trim();
      if (cleanedContent.startsWith('```json')) {
        cleanedContent = cleanedContent.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
      } else if (cleanedContent.startsWith('```')) {
        cleanedContent = cleanedContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      // 尝试提取 JSON 对象（支持多行）
      const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          parsed = JSON.parse(jsonMatch[0]);
          console.log('Successfully extracted JSON from response');
        } catch (extractError) {
          console.error('Failed to parse extracted JSON:', extractError);
          throw new Error('Invalid JSON response from API');
        }
      } else {
        console.error('No JSON object found in response. Content:', content.substring(0, 200));
        throw new Error('Invalid JSON response from API - no JSON object found');
      }
    }

    // 验证解析结果
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('API returned invalid JSON structure');
    }

    // 验证必需字段是否存在且有实际内容
    const hasValidRiskDescription = parsed.riskDescription &&
      parsed.riskDescription.trim() !== '' &&
      parsed.riskDescription !== '无法自动提取风险，请手动审查。' &&
      parsed.riskDescription !== 'Unable to auto-extract risks, please review manually.';

    if (!hasValidRiskDescription) {
      console.warn('API returned empty or default risk description:', parsed);
      // 如果所有字段都是默认值，说明 API 可能没有正确理解请求
      if (!parsed.category && !parsed.suggestedAction) {
        throw new Error('API did not provide meaningful analysis');
      }
    }

    // 确保 riskLevel 是有效值
    const validRiskLevels = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
    const riskLevel = validRiskLevels.includes(parsed.riskLevel?.toUpperCase())
      ? parsed.riskLevel.toUpperCase()
      : RiskLevel.LOW;

    return {
      category: parsed.category || (lang === 'zh' ? "法律分析" : "Legal Analysis"),
      riskLevel: riskLevel as RiskLevel,
      riskDescription: parsed.riskDescription || (lang === 'zh' ? "无法自动提取风险，请手动审查。" : "Unable to auto-extract risks, please review manually."),
      suggestedAction: parsed.suggestedAction || (lang === 'zh' ? "请人工审查此条款。" : "Please manually review this clause."),
      revisedText: parsed.revisedText || clauseText,
      thoughtChain: parsed.thoughtChain || (lang === 'zh'
        ? `【思维链】\n1. 识别条款类型：这是一个关于${parsed.category || "法律分析"}的条款\n2. 初步评估：条款内容涉及相关权利义务\n3. 风险识别：${riskLevel === 'LOW' ? '未发现明显风险' : '发现潜在风险点'}\n4. 风险评估：综合判断为${riskLevel}风险\n5. 建议形成：${parsed.suggestedAction || '请人工审查此条款。'}`
        : `【Thought Chain】\n1. Identify clause type: This is a clause about ${parsed.category || "Legal Analysis"}\n2. Initial assessment: The clause involves related rights and obligations\n3. Risk identification: ${riskLevel === 'LOW' ? 'No obvious risks found' : 'Potential risk points found'}\n4. Risk assessment: Overall judgment is ${riskLevel} risk\n5. Recommendation: ${parsed.suggestedAction || 'Verify contextual logic.'}`)
    };
  } catch (error: any) {
    console.error("AI Analysis failed:", error);
    console.error("Error details:", {
      message: error?.message,
      stack: error?.stack,
      clauseText: clauseText.substring(0, 100)
    });
    return {
      category: lang === 'zh' ? "法律分析" : "Legal Analysis",
      riskLevel: RiskLevel.LOW,
      riskDescription: lang === 'zh'
        ? `分析失败: ${error?.message || '未知错误'}。请检查控制台日志。`
        : `Analysis failed: ${error?.message || 'Unknown error'}. Please check console logs.`,
      suggestedAction: lang === 'zh' ? "请人工审查此条款。" : "Please manually review this clause.",
      revisedText: clauseText,
      thoughtChain: lang === 'zh'
        ? `【思维链】\n分析过程中出现错误：${error?.message || '未知错误'}`
        : `【Thought Chain】\nError occurred during analysis: ${error?.message || 'Unknown error'}`
    };
  }
}

export async function generateContractSummary(text: string, lang: Language): Promise<string> {
  try {
    const systemPrompt = lang === 'zh'
      ? "以专业、简洁的法律摘要形式输出。使用简体中文。"
      : "Output in professional, concise legal summary format. Use English.";

    const userPrompt = lang === 'zh'
      ? `请基于以下合同内容，提取前 3 个最致命的风险点，并给出整体评价：\n\n${text.substring(0, 10000)}`
      : `Based on the following contract, extract the top 3 most critical risks and give an overall assessment:\n\n${text.substring(0, 10000)}`;

    return await callOpenAI([
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt }
    ], undefined, false, DEFAULT_MODEL);
  } catch (error) {
    console.error("Summary generation failed:", error);
    return "";
  }
}

// 批量分析多个条款（一次 API 调用分析多个条款）
export async function analyzeClausesBatch(clauses: string[], lang: Language): Promise<Partial<ClauseAnalysis>[]> {
  if (clauses.length === 0) return [];

  try {
    const systemPrompt = lang === 'zh'
      ? `你是一位拥有20年经验的资深商业律师。你的分析应当尖锐、务实且具有保护性。必须使用简体中文。

你将收到多个合同条款，请对每个条款进行分析。你必须返回一个JSON数组，数组中每个对象对应一个条款的分析结果。

每个分析对象必须包含：
- clauseIndex: 条款的序号（从0开始）
- category: 条款的法律类别（如：责任限制、不可抗力、争议解决、保密条款等）
- riskLevel: 风险等级，必须是 LOW、MEDIUM、HIGH 或 CRITICAL 之一
- riskDescription: 该条款对己方可能造成的具体法律或商业损害说明
- suggestedAction: 具体的修改动作或谈判策略建议
- revisedText: 建议修订后的完整条款文本

只返回JSON数组，不要包含任何其他文本。`
      : `You are a senior commercial lawyer with 20 years of experience. Your analysis should be sharp, pragmatic, and protective. Must use English.

You will receive multiple contract clauses. Analyze each clause and return a JSON array where each object corresponds to one clause's analysis.

Each analysis object must contain:
- clauseIndex: the clause's index (starting from 0)
- category: legal category (e.g., liability limitation, force majeure, dispute resolution, confidentiality)
- riskLevel: must be LOW, MEDIUM, HIGH, or CRITICAL
- riskDescription: detailed explanation of potential legal or commercial harm
- suggestedAction: specific amendment action or negotiation strategy
- revisedText: full revised clause text using standard legal terminology

Return ONLY the JSON array, no other text.`;

    const clausesList = clauses.map((clause, index) =>
      `【条款 ${index + 1}】\n${clause}`
    ).join('\n\n---\n\n');

    const userPrompt = lang === 'zh'
      ? `请分析以下 ${clauses.length} 个合同条款：\n\n${clausesList}`
      : `Please analyze the following ${clauses.length} contract clauses:\n\n${clausesList}`;

    console.log(`Batch analyzing ${clauses.length} clauses in one API call...`);

    // 添加 JSON 格式要求，使用推理模型
    const content = await callOpenAI([
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt }
    ], { type: "json_object" }, false, DEFAULT_MODEL);

    console.log('Batch API Response:', content.substring(0, 500));

    // 解析响应
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch {
      // 尝试提取 JSON 数组
      let cleanedContent = content.trim();
      if (cleanedContent.startsWith('```json')) {
        cleanedContent = cleanedContent.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
      } else if (cleanedContent.startsWith('```')) {
        cleanedContent = cleanedContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      const arrayMatch = cleanedContent.match(/\[[\s\S]*\]/);
      if (arrayMatch) {
        parsed = JSON.parse(arrayMatch[0]);
      } else {
        // 尝试匹配单个对象（可能返回的是包装对象）
        const objMatch = cleanedContent.match(/\{[\s\S]*\}/);
        if (objMatch) {
          const obj = JSON.parse(objMatch[0]);
          // 如果是包装对象，提取数组
          parsed = obj.clauses || obj.analyses || obj.results || [obj];
        } else {
          throw new Error('Cannot extract JSON from response');
        }
      }
    }

    // 确保是数组
    const results = Array.isArray(parsed) ? parsed : (parsed.clauses || parsed.analyses || parsed.results || [parsed]);

    // 确保 riskLevel 有效
    const validRiskLevels = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

    // 创建一个按 clauseIndex 索引的 Map，确保分析结果与原始条款正确对应
    const analysesMap: Map<number, any> = new Map();

    // 检测 AI 是否从 1 开始计数（而不是 0）
    const clauseIndices = results
      .map((item: any) => typeof item.clauseIndex === 'number' ? item.clauseIndex : -1)
      .filter((idx: number) => idx >= 0);

    const minIndex = clauseIndices.length > 0 ? Math.min(...clauseIndices) : 0;
    const indexOffset = (minIndex === 1 && !clauseIndices.includes(0)) ? 1 : 0;

    results.forEach((item: any, arrayIndex: number) => {
      let targetIndex: number;
      if (typeof item.clauseIndex === 'number') {
        targetIndex = item.clauseIndex - indexOffset;
      } else {
        targetIndex = arrayIndex;
      }

      if (targetIndex >= 0 && targetIndex < clauses.length) {
        analysesMap.set(targetIndex, item);
      } else {
        analysesMap.set(arrayIndex, item);
      }
    });

    // 按照原始条款顺序构建分析结果数组
    return clauses.map((originalClause, index) => {
      const item = analysesMap.get(index) || {};

      const riskLevel = (validRiskLevels.includes(item.riskLevel?.toUpperCase())
        ? item.riskLevel.toUpperCase()
        : RiskLevel.LOW) as RiskLevel;

      // 根据风险级别提供合理的默认描述
      const getDefaultDescription = () => {
        if (item.riskDescription && item.riskDescription.trim()) return item.riskDescription;
        if (riskLevel === 'LOW') {
          return lang === 'zh'
            ? "【风险概述】该条款表述规范，风险可控\n【法律分析】条款符合一般商业惯例，无明显法律瑕疵\n【商业影响】对己方权益无不利影响"
            : "【Risk Overview】This clause is well-drafted with controlled risk\n【Legal Analysis】Compliant with general business practice, no obvious legal defects\n【Commercial Impact】No adverse impact on our interests";
        }
        return lang === 'zh' ? "无法自动提取风险，请手动审查。" : "Unable to auto-extract risks, please review manually.";
      };

      const getDefaultAction = () => {
        if (item.suggestedAction && item.suggestedAction.trim()) return item.suggestedAction;
        if (riskLevel === 'LOW') {
          return lang === 'zh'
            ? "【建议】可保留原文，无需修改"
            : "【Recommendation】Keep original text, no modification needed";
        }
        return lang === 'zh' ? "请人工审查此条款。" : "Please manually review this clause.";
      };

      const getDefaultThoughtChain = () => {
        if (item.thoughtChain && item.thoughtChain.trim()) return item.thoughtChain;
        return lang === 'zh' 
          ? `【思维链】\n1. 识别条款类型：这是一个关于${item.category || "法律分析"}的条款\n2. 初步评估：条款内容涉及相关权利义务\n3. 风险识别：${riskLevel === 'LOW' ? '未发现明显风险' : '发现潜在风险点'}\n4. 风险评估：综合判断为${riskLevel}风险\n5. 建议形成：${getDefaultAction()}`
          : `【Thought Chain】\n1. Identify clause type: This is a clause about ${item.category || "Legal Analysis"}\n2. Initial assessment: The clause involves related rights and obligations\n3. Risk identification: ${riskLevel === 'LOW' ? 'No obvious risks found' : 'Potential risk points found'}\n4. Risk assessment: Overall judgment is ${riskLevel} risk\n5. Recommendation: ${getDefaultAction()}`;
      };

      return {
        category: item.category || (lang === 'zh' ? "法律分析" : "Legal Analysis"),
        riskLevel,
        riskDescription: getDefaultDescription(),
        suggestedAction: getDefaultAction(),
        revisedText: item.revisedText || originalClause,
        thoughtChain: getDefaultThoughtChain()
      };
    });

  } catch (error: any) {
    console.error("Batch analysis failed:", error);
    // 返回默认结果
    return clauses.map(clause => ({
      category: lang === 'zh' ? "法律分析" : "Legal Analysis",
      riskLevel: RiskLevel.LOW,
      riskDescription: lang === 'zh'
        ? "【风险概述】该条款表述规范，风险可控\n【法律分析】条款符合一般商业惯例\n【商业影响】对己方权益无不利影响"
        : "【Risk Overview】This clause is well-drafted with controlled risk\n【Legal Analysis】Compliant with general business practice\n【Commercial Impact】No adverse impact on our interests",
      suggestedAction: lang === 'zh'
        ? "【建议】可保留原文，无需修改"
        : "【Recommendation】Keep original text, no modification needed",
      revisedText: clause,
      thoughtChain: lang === 'zh'
        ? `【思维链】\n1. 识别条款类型：这是一个关于法律分析的条款\n2. 初步评估：条款内容涉及相关权利义务\n3. 风险识别：未发现明显风险\n4. 风险评估：综合判断为LOW风险\n5. 建议形成：可保留原文，无需修改`
        : `【Thought Chain】\n1. Identify clause type: This is a clause about Legal Analysis\n2. Initial assessment: The clause involves related rights and obligations\n3. Risk identification: No obvious risks found\n4. Risk assessment: Overall judgment is LOW risk\n5. Recommendation: Keep original text, no modification needed`
    }));
  }
}

// 完整合同分析（一次 API 调用完成所有分析，包括摘要）
export async function analyzeFullContract(
  clauses: string[],
  lang: Language,
  companyPolicy?: string,
  config?: ReviewConfig
): Promise<{
  analyses: Partial<ClauseAnalysis>[];
  summary: string;
  legalReferences?: Array<{
    title: string;
    article: string;
    content: string;
    url?: string;
  }>;
}> {
  if (clauses.length === 0) return { analyses: [], summary: '', legalReferences: undefined };

  // 构建立场和力度描述
  const positionText = config?.position === 'partyA'
    ? (lang === 'zh' ? '甲方（合同发起方/服务提供方）' : 'Party A (Contract initiator/Service provider)')
    : (lang === 'zh' ? '乙方（合同接收方/服务接受方）' : 'Party B (Contract recipient/Service receiver)');

  const intensityDesc = {
    aggressive: lang === 'zh'
      ? '强势审查模式：以最大化保护己方利益为原则，对任何可能不利的条款从严评估，即使轻微不平衡也应标记为较高风险'
      : 'Aggressive mode: Maximize protection of our interests. Strictly evaluate any potentially unfavorable clauses. Mark even minor imbalances as higher risk.',
    neutral: lang === 'zh'
      ? '中立审查模式：平衡考虑双方利益，关注明显不合理或违法的条款，对行业惯例条款可适当放宽'
      : 'Neutral mode: Balance both parties interests. Focus on clearly unreasonable or illegal clauses. Industry-standard clauses can be more lenient.',
    defensive: lang === 'zh'
      ? '灵活审查模式：以促成交易为目标，仅关注关键风险点和违法条款，对次要问题可以接受'
      : 'Defensive mode: Focus on closing the deal. Only flag critical risks and illegal clauses. Minor issues are acceptable.'
  };

  const reviewConfigSection = config ? (lang === 'zh'
    ? `
═══════════════════════════════════════
【审查立场与策略】
═══════════════════════════════════════
📍 审查立场：${positionText}
⚡ 审查力度：${intensityDesc[config.intensity]}

请根据上述立场和力度调整风险评估标准和建议方向。
`
    : `
═══════════════════════════════════════
【Review Position & Strategy】
═══════════════════════════════════════
📍 Position: ${positionText}
⚡ Intensity: ${intensityDesc[config.intensity]}

Please adjust risk assessment and recommendations based on the above position and intensity.
`) : '';

  // 构建公司政策部分的提示词
  const companyPolicySection = companyPolicy && companyPolicy.trim()
    ? lang === 'zh'
      ? `
═══════════════════════════════════════
【特别关注：客户公司政策】
═══════════════════════════════════════
以下是客户公司的特定政策要求，请在审查时特别关注这些规则，任何违反这些政策的条款都应标记为较高风险：

${companyPolicy}

请确保在分析每个条款时，检查是否符合上述公司政策要求。
`
      : `
═══════════════════════════════════════
【SPECIAL ATTENTION: Client Custom Reviewlicy】
═══════════════════════════════════════
The following are the client company's specific policy requirements. Please pay special attention to these rules during review. Any clause violating these policies should be marked as higher risk:

${companyPolicy}

Please ensure that when analyzing each clause, you check compliance with the above Custom Reviewlicies.
`
    : '';

  try {
    const systemPrompt = lang === 'zh'
      ? `你是一位拥有20年经验的资深商业律师、合同审查专家和法律风险顾问。你将对合同进行系统性检查和评估，兼顾法律合规与商业可行性。必须使用简体中文。
${reviewConfigSection}${companyPolicySection}

═══════════════════════════════════════
【第一部分：合同审查目的】
═══════════════════════════════════════
1. 风险识别：发现可能导致经济损失、法律纠纷或责任不明的条款
2. 合法合规：确保条款符合《民法典》《合同法》等中国法律法规
3. 权利保护：确保己方在付款、知识产权、保密义务等方面的权益得到保护
4. 明确责任：明确双方的权利义务、违约责任和争议解决方式
5. 预防纠纷：识别可能引发未来争议的模糊或不公平条款

═══════════════════════════════════════
【第二部分：审查类别、典型条款与审查重点】
═══════════════════════════════════════
┌─────────────┬───────────────────────┬────────────────────────────────┐
│   类别      │      典型条款         │          审查重点               │
├─────────────┼───────────────────────┼────────────────────────────────┤
│ 保密条款    │ NDA、保密义务、期限   │ 是否合理、是否超期、范围是否过宽 │
│ 服务条款    │ 服务范围、交付成果    │ 交付时间、质量要求是否明确       │
│ 付款条款    │ 付款方式、延迟利息    │ 金额、时间、利率合法性           │
│ 知识产权    │ IP归属、使用权        │ 是否过度转让、是否保留必要权利   │
│ 违约条款    │ 赔偿责任、违约金      │ 范围是否合理、是否违法           │
│ 合同期限    │ 生效时间、终止条件    │ 是否可提前终止、是否有补偿       │
│ 法律管辖    │ 适用法律、争议解决    │ 是否符合企业习惯、风险可控       │
│ 责任限制    │ 免责条款、责任上限    │ 是否剥夺己方重要权利             │
│ 不可抗力    │ 定义范围、通知义务    │ 定义是否过宽或过窄               │
│ 陈述与保证  │ 声明事项、保证范围    │ 是否承担过多不可控保证           │
│ 终止与解除  │ 解约条件、后合同义务  │ 解约是否过于苛刻、清算是否明确   │
└─────────────┴───────────────────────┴────────────────────────────────┘

═══════════════════════════════════════
【第三部分：风险等级评估标准】
═══════════════════════════════════════
🔴 CRITICAL（致命风险）：
   - 可能导致重大经济损失（超过合同金额50%）
   - 可能引发法律诉讼或行政处罚
   - 条款可能无法执行或被认定无效
   - 严重违反法律法规强制性规定

🟠 HIGH（高风险）：
   - 明显不利于己方的权利义务失衡
   - 存在重大法律漏洞或模糊表述
   - 需立即修改，否则不建议签署

🟡 MEDIUM（中风险）：
   - 存在模糊或不公平之处
   - 建议协商修改以降低风险
   - 可接受但需记录在案

🟢 LOW（低风险）：
   - 基本合理、符合行业惯例
   - 可接受或仅需小幅文字调整

═══════════════════════════════════════
【第四部分：审查流程】
═══════════════════════════════════════
1. 条款识别：判断每个条款属于哪个类别
2. 风险评估：根据标准标记风险等级
3. 危害分析：说明对己方的具体法律/商业危害
4. 修改建议：提出具体的修改动作或谈判策略
5. 条款重写：提供符合法律术语规范的修订文本

═══════════════════════════════════════
【输出要求】
═══════════════════════════════════════
⚠️ 重要：必须为每一个条款返回分析结果，不能跳过任何条款！即使是 LOW 风险的条款，也必须提供完整的分析内容。

⚠️ 关键要求：你必须返回一个有效的 JSON 对象，且仅包含 JSON，不要包含任何 markdown 代码块标记（如 \`\`\`json 或 \`\`\`），不要包含任何解释性文字，直接返回 JSON 对象。

⚠️ 思维链要求：thoughtChain 字段非常重要！请详细展示你的分析思考过程，包括每一步的推理逻辑、法律依据、风险评估依据等。不要使用模板化的内容，要根据实际条款内容进行真实的分析思考。作为推理模型，你应该展示完整的推理过程，包括你的思考步骤、法律条文引用、风险评估逻辑等。

返回纯JSON对象（不要包含任何其他文本），结构如下：
{
  "clauses": [
    {
      "clauseIndex": 0,
      "category": "类别名称",
      "riskLevel": "CRITICAL/HIGH/MEDIUM/LOW",
      "riskDescription": "【风险概述】一句话总结风险点或合规性\\n【法律分析】具体法律问题分析（LOW风险则说明为何合规）\\n【商业影响】对己方的商业影响评估",
      "suggestedAction": "【建议】具体的保留/修改/删除建议（LOW风险可建议保留原文或小幅优化）",
      "revisedText": "修订后的完整条款文本（LOW风险可保持原文或仅做文字润色）",
      "thoughtChain": "【思维链】请详细展示你的分析思考过程，包括：\\n1. 识别条款类型：这是关于[类别]的条款，因为[具体原因]\\n2. 初步评估：条款内容涉及[关键权利义务点]，初步判断[初步结论]\\n3. 风险识别：通过分析发现[具体风险点]，该风险可能导致的后果是[具体后果]\\n4. 法律依据：根据《民法典》第X条/《合同法》等相关法律，[详细法律分析]\\n5. 风险评估：综合考虑法律风险、商业影响、执行难度等因素，综合判断为[风险等级]风险\\n6. 建议形成：基于以上分析，建议[具体行动]，理由：[详细理由]",
      "legalReferences": ["《民法典》第X条：相关法律条文内容"]
    }
  ],
  "summary": "【合同整体风险评级】\\n\\n【致命风险TOP 3】\\n1. [风险描述及条款位置]\\n2. [风险描述及条款位置]\\n3. [风险描述及条款位置]\\n\\n【核心谈判要点】\\n- 必须坚持的条款修改\\n- 可让步的条款范围\\n\\n【签署建议】\\n是否建议签署及前提条件",
  "legalReferences": [
    {
      "title": "《民法典》",
      "article": "第五百七十七条",
      "content": "当事人一方不履行合同义务或者履行合同义务不符合约定的，应当承担继续履行、采取补救措施或者赔偿损失等违约责任。",
      "url": "https://www.chinalaw.gov.cn/"
    }
  ]
}

注意：clauses 数组长度必须等于输入的条款数量！`
      : `You are a senior commercial lawyer, contract review expert, and legal risk advisor with 20 years of experience. You will conduct a systematic examination and assessment of contracts, considering both legal compliance and commercial viability. Must use English.
${companyPolicySection}
═══════════════════════════════════════
【PART 1: Contract Review Objectives】
═══════════════════════════════════════
1. Risk Identification: Discover clauses that may cause financial loss, legal disputes, or unclear liability
2. Legal Compliance: Ensure clauses comply with applicable laws and regulations
3. Rights Protection: Protect our party's interests in payment, IP, confidentiality, etc.
4. Clear Responsibilities: Clarify rights, obligations, breach liability, and dispute resolution
5. Dispute Prevention: Identify ambiguous or unfair clauses that may cause future disputes

═══════════════════════════════════════
【PART 2: Review Categories & Focus Areas】
═══════════════════════════════════════
┌──────────────────┬─────────────────────────┬──────────────────────────────────┐
│    Category      │    Typical Clauses      │        Review Focus              │
├──────────────────┼─────────────────────────┼──────────────────────────────────┤
│ Confidentiality  │ NDA, obligations, term  │ Reasonable scope, excessive term │
│ Service Terms    │ Scope, deliverables     │ Clear delivery time & quality    │
│ Payment Terms    │ Methods, late interest  │ Lawful amounts, timing, rates    │
│ IP Rights        │ IP ownership, usage     │ Over-transfer, retained rights   │
│ Breach Terms     │ Damages, penalties      │ Reasonable scope, lawfulness     │
│ Contract Term    │ Effective date, term    │ Early termination, compensation  │
│ Jurisdiction     │ Governing law, disputes │ Company practice, controllable   │
│ Liability Limit  │ Exclusions, caps        │ Depriving important rights       │
│ Force Majeure    │ Definition, notice      │ Too broad or too narrow          │
│ Representations  │ Warranties, scope       │ Uncontrollable guarantees        │
│ Termination      │ Exit conditions, duties │ Harsh conditions, clear settle   │
└──────────────────┴─────────────────────────┴──────────────────────────────────┘

═══════════════════════════════════════
【PART 3: Risk Level Assessment Standards】
═══════════════════════════════════════
🔴 CRITICAL:
   - May cause major financial loss (>50% of contract value)
   - May lead to litigation or regulatory penalties
   - Clause may be unenforceable or void
   - Serious violation of mandatory legal provisions

🟠 HIGH:
   - Clearly unfavorable rights/obligations imbalance
   - Major legal loopholes or ambiguous wording
   - Requires immediate modification before signing

🟡 MEDIUM:
   - Contains ambiguity or unfairness
   - Negotiation recommended to reduce risk
   - Acceptable but should be documented

🟢 LOW:
   - Generally reasonable, follows industry practice
   - Acceptable or needs only minor adjustments

═══════════════════════════════════════
【PART 4: Review Process】
═══════════════════════════════════════
1. Clause Identification: Determine category of each clause
2. Risk Assessment: Mark risk level per standards
3. Harm Analysis: Explain specific legal/commercial harm to our party
4. Modification Advice: Propose specific changes or negotiation strategy
5. Clause Rewrite: Provide revised text with proper legal terminology

═══════════════════════════════════════
【OUTPUT REQUIREMENTS】
═══════════════════════════════════════
⚠️ IMPORTANT: You MUST return analysis for EVERY clause! Do not skip any clause, even if it's LOW risk. Every clause needs complete analysis content.

⚠️ CRITICAL: You must return a valid JSON object ONLY. Do not include any markdown code block markers (like \`\`\`json or \`\`\`), do not include any explanatory text. Return the JSON object directly.

⚠️ THOUGHT CHAIN REQUIREMENT: The thoughtChain field is very important! Please provide a detailed analysis thinking process, including reasoning logic, legal basis, risk assessment basis for each step. Do not use templated content, but conduct real analysis thinking based on the actual clause content. As a reasoning model, you should show your complete reasoning process, including thinking steps, legal references, risk assessment logic, etc.

Return ONLY a JSON object (no other text) with this structure:
{
  "clauses": [
    {
      "clauseIndex": 0,
      "category": "Category Name",
      "riskLevel": "CRITICAL/HIGH/MEDIUM/LOW",
      "riskDescription": "【Risk Overview】One sentence risk/compliance summary\\n【Legal Analysis】Specific legal issues (for LOW risk, explain why it's compliant)\\n【Commercial Impact】Business impact assessment",
      "suggestedAction": "【Recommendation】Specific keep/modify/delete advice (for LOW risk, can recommend keeping original or minor polish)",
      "revisedText": "Complete revised clause text (for LOW risk, can keep original or just polish wording)",
      "thoughtChain": "【Thought Chain】Please provide a detailed analysis thinking process, including:\\n1. Identify clause type: This is a clause about [category] because [specific reason]\\n2. Initial assessment: The clause involves [key rights/obligations], initial judgment [preliminary conclusion]\\n3. Risk identification: Through analysis found [specific risk], potential consequences are [specific consequences]\\n4. Legal basis: According to [law/article] and related laws, [detailed legal analysis]\\n5. Risk assessment: Comprehensive consideration of legal risk, commercial impact, execution difficulty, overall judgment is [risk level] risk\\n6. Recommendation: Based on the above analysis, suggest [specific action], reason: [detailed reason]"
    }
  ],
  "summary": "【Overall Contract Risk Rating】\\n\\n【Top 3 Critical Risks】\\n1. [Risk description & clause location]\\n2. [Risk description & clause location]\\n3. [Risk description & clause location]\\n\\n【Key Negotiation Points】\\n- Must-have clause modifications\\n- Acceptable concessions\\n\\n【Signing Recommendation】\\nWhether to sign and prerequisites"
}

Note: The clauses array length MUST equal the number of input clauses!`;

    const clausesList = clauses.map((clause, index) =>
      `【条款 ${index + 1}】\n${clause}`
    ).join('\n\n---\n\n');

    const userPrompt = lang === 'zh'
      ? `请分析以下合同（共 ${clauses.length} 个条款）并提供完整的风险评估：\n\n${clausesList}`
      : `Please analyze this contract (${clauses.length} clauses) and provide a complete risk assessment:\n\n${clausesList}`;

    console.log(`Full contract analysis: ${clauses.length} clauses in ONE API call...`);
    console.log(`Using model: ${DEFAULT_MODEL} (reasoning model with native thinking support)`);

    // 添加 JSON 格式要求，并启用思维链功能
    // 注意：推理模型（gpt-5-thinking）原生支持思维链，但可能不支持 response_format
    // 所以我们在提示词中强调返回 JSON，如果 response_format 不支持会自动回退
    const content = await callOpenAI([
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt }
    ], { type: "json_object" }, true, DEFAULT_MODEL); // 使用推理模型并启用思维链

    console.log('Full contract API Response:', content.substring(0, 500));

    // 解析响应
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch {
      let cleanedContent = content.trim();
      if (cleanedContent.startsWith('```json')) {
        cleanedContent = cleanedContent.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
      } else if (cleanedContent.startsWith('```')) {
        cleanedContent = cleanedContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      const objMatch = cleanedContent.match(/\{[\s\S]*\}/);
      if (objMatch) {
        parsed = JSON.parse(objMatch[0]);
      } else {
        throw new Error('Cannot extract JSON from response');
      }
    }

    const validRiskLevels = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
    const clauseResults = parsed.clauses || parsed.analyses || [];

    console.log(`AI 返回了 ${clauseResults.length} 个条款分析，原始条款数量: ${clauses.length}`);

    // 创建一个按 clauseIndex 索引的数组，确保分析结果与原始条款正确对应
    const analysesMap: Map<number, any> = new Map();

    // 检测 AI 是否从 1 开始计数（而不是 0）
    const clauseIndices = clauseResults
      .map((item: any) => typeof item.clauseIndex === 'number' ? item.clauseIndex : -1)
      .filter((idx: number) => idx >= 0);

    // 如果所有返回的 clauseIndex 都 >= 1 且没有 0，说明 AI 从 1 开始计数
    const minIndex = clauseIndices.length > 0 ? Math.min(...clauseIndices) : 0;
    const indexOffset = (minIndex === 1 && !clauseIndices.includes(0)) ? 1 : 0;

    if (indexOffset > 0) {
      console.log('检测到 AI 从 1 开始计数，自动调整索引偏移');
    }

    clauseResults.forEach((item: any, arrayIndex: number) => {
      // 优先使用 AI 返回的 clauseIndex（减去偏移量），否则使用数组顺序
      let targetIndex: number;
      if (typeof item.clauseIndex === 'number') {
        targetIndex = item.clauseIndex - indexOffset;
      } else {
        targetIndex = arrayIndex;
      }

      // 确保索引在有效范围内
      if (targetIndex >= 0 && targetIndex < clauses.length) {
        analysesMap.set(targetIndex, item);
      } else {
        console.warn(`条款索引 ${item.clauseIndex} 超出范围，使用数组顺序 ${arrayIndex}`);
        analysesMap.set(arrayIndex, item);
      }
    });

    // 按照原始条款顺序构建分析结果数组
    const analyses = clauses.map((originalClause, index) => {
      const item = analysesMap.get(index) || {};

      const riskLevel = (validRiskLevels.includes(item.riskLevel?.toUpperCase())
        ? item.riskLevel.toUpperCase()
        : RiskLevel.LOW) as RiskLevel;

      // 根据风险级别提供合理的默认描述
      const getDefaultDescription = () => {
        if (item.riskDescription && item.riskDescription.trim()) return item.riskDescription;
        if (riskLevel === 'LOW') {
          return lang === 'zh'
            ? "【风险概述】该条款表述规范，风险可控\n【法律分析】条款符合一般商业惯例，无明显法律瑕疵\n【商业影响】对己方权益无不利影响"
            : "【Risk Overview】This clause is well-drafted with controlled risk\n【Legal Analysis】Compliant with general business practice, no obvious legal defects\n【Commercial Impact】No adverse impact on our interests";
        }
        return lang === 'zh' ? "无法自动提取风险，请手动审查。" : "Unable to auto-extract risks, please review manually.";
      };

      const getDefaultAction = () => {
        if (item.suggestedAction && item.suggestedAction.trim()) return item.suggestedAction;
        if (riskLevel === 'LOW') {
          return lang === 'zh'
            ? "【建议】可保留原文，无需修改"
            : "【Recommendation】Keep original text, no modification needed";
        }
        return lang === 'zh' ? "请人工审查此条款。" : "Please manually review this clause.";
      };

      const getDefaultThoughtChain = () => {
        if (item.thoughtChain && item.thoughtChain.trim()) return item.thoughtChain;
        return lang === 'zh' 
          ? `【思维链】\n1. 识别条款类型：这是一个关于${item.category || "法律分析"}的条款\n2. 初步评估：条款内容涉及相关权利义务\n3. 风险识别：${riskLevel === 'LOW' ? '未发现明显风险' : '发现潜在风险点'}\n4. 风险评估：综合判断为${riskLevel}风险\n5. 建议形成：${getDefaultAction()}`
          : `【Thought Chain】\n1. Identify clause type: This is a clause about ${item.category || "Legal Analysis"}\n2. Initial assessment: The clause involves related rights and obligations\n3. Risk identification: ${riskLevel === 'LOW' ? 'No obvious risks found' : 'Potential risk points found'}\n4. Risk assessment: Overall judgment is ${riskLevel} risk\n5. Recommendation: ${getDefaultAction()}`;
      };

      return {
        category: item.category || (lang === 'zh' ? "法律分析" : "Legal Analysis"),
        riskLevel,
        riskDescription: getDefaultDescription(),
        suggestedAction: getDefaultAction(),
        revisedText: item.revisedText || originalClause,
        thoughtChain: getDefaultThoughtChain()
      };
    });

    console.log(`分析结果匹配: ${clauseResults.length} 个AI分析 → ${clauses.length} 个原始条款`);

    // 提取法律条款引用
    const legalReferences: Array<{ title: string; article: string; content: string; url?: string }> = [];
    
    // 清理 article 字段的辅助函数
    const cleanArticle = (article: string): string => {
      if (!article) return '';
      let cleaned = article.trim();
      // 移除开头的"第"（可能多个）
      cleaned = cleaned.replace(/^第+/, '');
      // 移除结尾的"条"（可能多个）
      cleaned = cleaned.replace(/条+$/, '');
      return cleaned.trim();
    };

    // 从 summary 和 clauses 中提取法律条款引用
    if (parsed.legalReferences && Array.isArray(parsed.legalReferences)) {
      parsed.legalReferences.forEach((ref: any) => {
        if (typeof ref === 'object' && ref.title && ref.article) {
          legalReferences.push({
            title: ref.title,
            article: cleanArticle(ref.article),
            content: ref.content || '',
            url: ref.url || 'https://www.chinalaw.gov.cn/'
          });
        }
      });
    }

    // 从每个条款的 legalReferences 中提取
    clauseResults.forEach((item: any) => {
      if (item.legalReferences && Array.isArray(item.legalReferences)) {
        item.legalReferences.forEach((ref: any) => {
          if (typeof ref === 'string') {
            // 如果是字符串格式，尝试解析
            const match = ref.match(/(《[^》]+》)[第]*([^：:]+)[：:](.+)/);
            if (match) {
              legalReferences.push({
                title: match[1],
                article: cleanArticle(match[2]),
                content: match[3].trim(),
                url: 'https://www.chinalaw.gov.cn/'
              });
            }
          } else if (typeof ref === 'object' && ref.title && ref.article) {
            legalReferences.push({
              title: ref.title,
              article: cleanArticle(ref.article),
              content: ref.content || '',
              url: ref.url || 'https://www.chinalaw.gov.cn/'
            });
          }
        });
      }
    });

    // 去重
    const uniqueReferences = legalReferences.filter((ref, index, self) =>
      index === self.findIndex(r => r.article === ref.article && r.title === ref.title)
    );

    return {
      analyses,
      summary: parsed.summary || '',
      legalReferences: uniqueReferences.length > 0 ? uniqueReferences : undefined
    };

  } catch (error: any) {
    console.error("Full contract analysis failed:", error);
    return {
      analyses: clauses.map(clause => ({
        category: lang === 'zh' ? "法律分析" : "Legal Analysis",
        riskLevel: RiskLevel.LOW,
        riskDescription: lang === 'zh'
          ? `分析失败: ${error?.message || '未知错误'}`
          : `Analysis failed: ${error?.message || 'Unknown error'}`,
        suggestedAction: lang === 'zh' ? "请人工审查此条款。" : "Please manually review this clause.",
        revisedText: clause,
        thoughtChain: lang === 'zh' 
          ? `【思维链】\n分析过程中出现错误：${error?.message || '未知错误'}`
          : `【Thought Chain】\nError occurred during analysis: ${error?.message || 'Unknown error'}`
      })),
      summary: lang === 'zh' ? '分析失败，请重试。' : 'Analysis failed, please retry.',
      legalReferences: undefined
    };
  }
}
